// image-loader.svelte.js
// Gates binder card image fetches so the visible spread loads before the
// buffered pages behind it. Tiers come from logic/image-load-order.js; this
// store counts what is waiting and in flight per tier and exposes the single
// tier currently allowed to start. See IMAGE_LOAD_ORDER.md.

import { onDestroy, untrack } from 'svelte';
import { IMAGE_TIER, IMAGE_TIER_COUNT, computeOpenTier } from '../logic/image-load-order.js';
import { IMAGE_TIER_MAX_WAIT_MS } from '../logic/config.js';

export const imageLoadState = $state({
  /** In-flight image count per tier. */
  pending: new Array(IMAGE_TIER_COUNT).fill(0),
  /** Queued-but-not-started image count per tier. */
  waiting: new Array(IMAGE_TIER_COUNT).fill(0),
  /** Per tier: true once it overran IMAGE_TIER_MAX_WAIT_MS with images still in flight. */
  forced: new Array(IMAGE_TIER_COUNT).fill(false),
  /**
   * False while a batch of slots is still announcing itself (mount, page flip).
   * Slots mount in DOM order, cover first, so a far-tier slot could otherwise
   * see an empty store and start before the visible spread has registered.
   */
  settled: true,
});

/** @type {(ReturnType<typeof setTimeout> | null)[]} */
const forceTimers = new Array(IMAGE_TIER_COUNT).fill(null);
/** @type {ReturnType<typeof setTimeout> | null} */
let settleTimer = null;

const openTier = $derived(
  imageLoadState.settled
    ? computeOpenTier(imageLoadState.pending, imageLoadState.waiting, imageLoadState.forced)
    : IMAGE_TIER.VISIBLE
);

/** The highest tier allowed to start a fetch right now. Reactive inside $effect/$derived. */
export function getOpenTier() {
  return openTier;
}

function unsettle() {
  imageLoadState.settled = false;
  if (settleTimer !== null) return;
  settleTimer = setTimeout(() => {
    settleTimer = null;
    imageLoadState.settled = true;
  }, 0);
}

/**
 * Announce a slot that has an image to fetch but is not yet allowed to start.
 * @returns {() => void} unregister; call when the slot starts, changes tier, or unmounts
 */
export function registerWaiting(tier) {
  // Callers run inside $effect: mutate without adding the counters to the
  // effect's dependencies, or the effect re-runs on its own write.
  untrack(() => {
    imageLoadState.waiting[tier] += 1;
    unsettle();
  });
  let done = false;
  return () => {
    if (done) return;
    done = true;
    untrack(() => {
      imageLoadState.waiting[tier] -= 1;
    });
  };
}

/**
 * Record that one image of the given tier has started loading.
 * @returns {() => void} idempotent release, call on load or error (or unmount)
 */
export function trackImageLoad(tier) {
  return untrack(() => {
    // A fresh batch gets a fresh budget: an earlier overrun must not leave the
    // tier permanently unblocking on a slow connection.
    imageLoadState.forced[tier] = false;
    if (forceTimers[tier] === null) {
      forceTimers[tier] = setTimeout(() => {
        forceTimers[tier] = null;
        if (imageLoadState.pending[tier] > 0) {
          imageLoadState.forced[tier] = true;
          console.log(
            `[ImageLoader] Tier ${tier} overran ${IMAGE_TIER_MAX_WAIT_MS}ms; opening later tiers`
          );
        }
      }, IMAGE_TIER_MAX_WAIT_MS);
    }
    imageLoadState.pending[tier] += 1;

    let released = false;
    return () => {
      if (released) return;
      released = true;
      untrack(() => {
        imageLoadState.pending[tier] -= 1;
        if (imageLoadState.pending[tier] === 0) {
          imageLoadState.forced[tier] = false;
          if (forceTimers[tier] !== null) {
            clearTimeout(forceTimers[tier]);
            forceTimers[tier] = null;
          }
        }
      });
    };
  });
}

/**
 * One card slot's place in the queue. Call during component init.
 * `getTier` and `getUrl` are read reactively: a tier change re-queues the
 * slot, a URL change (re-sort, cache refresh) drops the old load and queues
 * the new image like any other, and unmount releases whatever is in flight.
 * @param {() => number} getTier
 * @param {() => string | null} getUrl
 */
export function createSlotImageLoad(getTier, getUrl) {
  /** @type {{ url: string, release: () => void } | null} */
  let load = $state.raw(null);

  // Announce the slot as waiting at its tier so later tiers stay shut until
  // it starts. Deliberately blind to the open tier, or the re-registration
  // would unsettle the store it is reacting to.
  $effect(() => {
    const url = getUrl();
    if (!url || load?.url === url) return;
    return registerWaiting(getTier());
  });

  // Start once the tier is open. A started slot depends only on its URL, and a
  // queued one only on its tier and the store's single open-tier value, so
  // slots re-run once per tier change, not once per image event.
  $effect(() => {
    const url = getUrl();
    if (load !== null && load.url !== url) {
      load.release();
      load = null;
    }
    if (load !== null || !url) return;
    const tier = getTier();
    if (tier > getOpenTier()) return;
    load = { url, release: trackImageLoad(tier) };
  });

  onDestroy(() => load?.release());

  return {
    /** True once the <img> may carry its src. */
    get started() {
      return load !== null;
    },
    /** Call on the <img>'s load or error. */
    release() {
      load?.release();
    },
  };
}

/** Test helper: clear counts and timers. */
export function resetImageLoader() {
  for (let t = 0; t < IMAGE_TIER_COUNT; t++) {
    imageLoadState.pending[t] = 0;
    imageLoadState.waiting[t] = 0;
    imageLoadState.forced[t] = false;
    if (forceTimers[t] !== null) {
      clearTimeout(forceTimers[t]);
      forceTimers[t] = null;
    }
  }
  if (settleTimer !== null) {
    clearTimeout(settleTimer);
    settleTimer = null;
  }
  imageLoadState.settled = true;
}
